import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle, Cookie, ShieldCheck, Wallet } from 'lucide-react';
import { relTime } from './helpers';

/**
 * Big visual status header for a single platform tab.
 */
export function PlatformStatusHeader({ platform }) {
    const p = platform;
    const isConnected = p.last_test_status === 'CONNECTED';
    const hasCookies = (p.cookies_count || 0) > 0;

    const tone = isConnected
        ? 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white'
        : hasCookies
            ? 'border-amber-200 bg-gradient-to-br from-amber-50 to-white'
            : 'border-border bg-gradient-to-br from-secondary/30 to-white';

    const iconTone = isConnected
        ? 'bg-emerald-500 text-white'
        : hasCookies
            ? 'bg-amber-500 text-white'
            : 'bg-slate-200 text-slate-600';

    const Icon = isConnected ? CheckCircle2 : hasCookies ? AlertCircle : Cookie;

    const title = isConnected
        ? 'Real recharge LIVE'
        : hasCookies
            ? 'Session uploaded \u2014 needs test'
            : 'No session yet';

    const subtitle = isConnected
        ? `Recharges are processed directly on ${p.name} using your authenticated session.`
        : hasCookies
            ? `Cookies are stored but the last test didn't confirm a working session.`
            : `Upload a cookies.txt file from ${p.name} to enable real recharges.`;

    return (
        <div className={`relative overflow-hidden rounded-2xl p-5 sm:p-6 border ${tone}`}>
            <div className="flex items-start gap-3">
                <div className={`shrink-0 h-12 w-12 rounded-2xl flex items-center justify-center ${iconTone}`}>
                    <Icon className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="font-display text-xl sm:text-2xl font-semibold">{title}</div>
                        {p.is_native && (
                            <Badge className="bg-[#0E2A47] hover:bg-[#0E2A47]">
                                <ShieldCheck className="h-3 w-3 mr-1" />Native bridge
                            </Badge>
                        )}
                    </div>
                    <div className="text-sm text-muted-foreground mt-0.5">{subtitle}</div>
                    {isConnected && p.last_wallet_preview && (
                        <div className="mt-3 inline-flex items-center gap-2 bg-white/70 border border-emerald-200 rounded-lg px-3 py-1.5 text-xs">
                            <Wallet className="h-3.5 w-3.5 text-emerald-700" />
                            <span className="font-mono text-emerald-900">{p.last_wallet_preview}</span>
                        </div>
                    )}
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                        <span>Cookies: <strong className="text-foreground">{p.cookies_count || 0}</strong></span>
                        <span>Last refreshed: <strong className="text-foreground">{relTime(p.cookies_updated_at)}</strong></span>
                        <span>Last tested: <strong className="text-foreground">{relTime(p.last_tested_at)}</strong></span>
                        {p.last_test_message && (
                            <span>Status message: <strong className="text-foreground">{p.last_test_message}</strong></span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PlatformStatusHeader;
